import {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cc from 'classcat';
import { UtilsAccount } from 'utils/account';
import { platform } from 'utils/bom-dom-manipulation/platform';
import { gameMapper } from 'utils/casino/maps';
import { ZINDEX_HIGH_3, ZINDEX_HIGH_5 } from 'utils/constants/app/ui';
import { CUSTOM_CATEGORIES } from 'utils/constants/casino/custom-categories';
import { CASINO_MULTIVIEW_ENABLED_QUERY_VALUE } from 'utils/constants/casino/url-queries';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import {
  ECasinoGameMode,
  ECasinoGameView,
  ECasinoGameViewOrder,
  ICasinoCachedGames
} from 'interfaces/casino-data';
import { NewCasinoGame } from 'interfaces/new-casino';
import { fetchGamesByIds } from 'services/casino/casino-fetch-games-by-id';
import { CasinoLimitModal } from './__partials/CasinoLimitModal';
import { GameView } from './__partials/GameView';
import { MultiviewGamesSameGameNotice } from './__partials/MultiviewGamesSameGameNotice';
import { MultiviewSwitcher } from './__partials/MultiviewSwitcher';
import { CasinoDom } from './constants/dom-variables';
import { MultiviewManager } from './managers/MultiviewManager';
import { SingleviewManager } from './managers/SingleviewManager';
import { CasinoGameViewContainerProvider } from './providers/GameViewContainerProvider';
import { UtilsCasino } from './utils';
import { Modal } from 'components/Modal';
import { addQuery } from 'components/QueryRoute';
import { Portal } from 'components/Shared/Portal';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useSearchParams } from 'hooks/useSearchParams';
import { useHeaderVisibility } from 'newhooks/generic/useHeaderVisibility';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { useValueReference } from 'newhooks/generic/useValueReference';
import { CasinoOpenGameConfigsProvider } from 'providers/casino/openGameConfigsProvider';
import { batchActions } from 'store/actions';
import {
  addCachedGame,
  addCachedGameId,
  setCachedGameActive,
  setCachedGameMode,
  setCachedGameOrder
} from 'store/actions/new-casino';
import { getHeaderState } from 'store/selectors/app-data';
import { getActiveGames } from 'store/selectors/new-casino';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';
import { useCsMetaUpdates } from '../hooks/useCsMetaUpdates';
import './index.less';

if (isMobile()) {
  import('./mobile.less');
} else {
  import('./desktop.less');
}

const SORTED_GAMES_UPDATE_DEBOUNCE_VALUE = 100;
const IS_IOS = !!(platform && /iPhone/.test(platform));

export const GameViewContainer = () => {
  useCsMetaUpdates();
  useHeaderVisibility();

  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const { params: routeParams } = useSbRouteMatch();
  const searchParams = useSearchParams();
  const [sameGameNoticeOpen, setSameGameNoticeOpen] = useState(false);
  const [activeView, setActiveView] = useState(() => {
    return ECasinoGameView.SINGLE;
  });

  const isLoggedIn = useSelector(getIsLoggedIn);

  const activeViewReference = useValueReference(activeView);

  const searchParamsModeForMobile = isMobile() ? searchParams.mode : '';

  const headerState = useSelector(getHeaderState);
  const activeGames = useSelector(getActiveGames) as ICasinoCachedGames;
  const queries = useSearchParams();

  const { height: headerHeight, isVisible: isHeaderVisible } = headerState;
  const externalCategory = routeParams?.gameCategory;
  const mainCategory = routeParams?.category || CUSTOM_CATEGORIES.all.id;
  const isThereAnyActiveGame = useMemo(
    () =>
      Object.keys(activeGames).length > 0 &&
      (SpringConfigs.CASINO_FUN_MODE || isLoggedIn),
    [activeGames, queries]
  );

  const sortedActiveGames = useMemo(() => {
    return Object.values(activeGames).sort(
      (gameA, gameB) => gameA.order - gameB.order
    );
  }, [activeGames]);

  const [debouncedSortedActiveGames] = useDebouncedValue(
    sortedActiveGames,
    SORTED_GAMES_UPDATE_DEBOUNCE_VALUE
  );

  const heightStyle = useMemo(
    () => ({
      height: `calc(100vh - ${headerHeight}px)`
    }),
    [headerHeight]
  );

  const maskStyle = useMemo(
    () => ({
      background: 'var(--v3-black-4)',
      height: `calc(100vh - ${headerHeight}px)`,
      marginTop: `${headerHeight}px`
    }),
    [headerHeight]
  );

  /**  Handlers  **/

  const handleOpenGame = useCallback(
    (game: NewCasinoGame, mode: ECasinoGameMode, gameIndexToChange: 0 | 1) => {
      UtilsCasino.constructAndRedirectToGameView({
        game,
        mode,
        view: activeViewReference.current,
        selectedCategory: routeParams?.category,
        gameIndexToChange
      });
    },
    [routeParams?.category]
  );

  const handleOpenGameMain = useCallback(
    (game: NewCasinoGame, mode: ECasinoGameMode) => {
      if (activeGames[game.extearnal_game_id]) {
        setSameGameNoticeOpen(true);

        return;
      }

      handleOpenGame(game, mode, 0);
    },
    [handleOpenGame, activeGames]
  );

  const handleOpenGameSecondary = useCallback(
    (game: NewCasinoGame, mode: ECasinoGameMode) => {
      if (activeGames[game.extearnal_game_id]) {
        setSameGameNoticeOpen(true);

        return;
      }

      handleOpenGame(game, mode, 1);
    },
    [handleOpenGame, activeGames]
  );

  const handleModeChangeMain = useCallback(
    (game: NewCasinoGame, mode: ECasinoGameMode) => {
      handleOpenGame(game, mode, 0);
    },
    [handleOpenGame]
  );

  const handleModeChangeSecondary = useCallback(
    (game: NewCasinoGame, mode: ECasinoGameMode) => {
      handleOpenGame(game, mode, 1);
    },
    [handleOpenGame]
  );

  /**  Effects  **/

  // handling game processing in case of single view

  useEffect(() => {
    if (activeViewReference.current !== ECasinoGameView.SINGLE) {
      return;
    }

    let [, gameExternalId] = (routeParams?.game || '').split('-');

    gameExternalId = searchParams?.gameId || gameExternalId;

    if (!gameExternalId) {
      return;
    }

    const checkAndAddGameInCache = () => {
      const isGameInCache =
        UtilsCasino.checkIsGameAlreadyCached(gameExternalId);

      if (isGameInCache) {
        dispatch(setCachedGameActive(gameExternalId));

        return;
      }

      // preserving the game id in the store
      dispatch(addCachedGameId(gameExternalId));
      fetchGamesByIds(
        [gameExternalId],
        true,
        true,
        !!SpringConfigs.DECENTRALIZED_CASINO_URL
      ).then(games => {
        const game = games[0];

        if (game) {
          const hasGameFunMode = !!game.types.funMode;

          dispatch(addCachedGame(gameMapper(game)));

          if (searchParams.mode !== ECasinoGameMode.REAL && !hasGameFunMode) {
            history.push(addQuery({ mode: ECasinoGameMode.REAL }));
            dispatch(
              setCachedGameMode({
                id: gameExternalId,
                mode: ECasinoGameMode.REAL
              })
            );
          }
        }
      });
    };

    checkAndAddGameInCache();
  }, [routeParams?.game, searchParams?.gameId]);

  // handling game processing in case of multi view
  useLayoutEffect(() => {
    if (searchParams.multiview !== CASINO_MULTIVIEW_ENABLED_QUERY_VALUE) {
      return;
    }

    const gamesInfo = UtilsCasino.extractGameInfoFromSearchParams(searchParams);

    if (gamesInfo.length === 0) {
      return;
    }

    const checkAndAddGamesInCache = () => {
      const notCachedGameIds = gamesInfo.map(gameInfo => {
        const isGameInCache = UtilsCasino.checkIsGameAlreadyCached(gameInfo.id);

        if (isGameInCache) {
          dispatch(
            batchActions(
              setCachedGameActive(gameInfo.id),
              setCachedGameOrder({
                id: gameInfo.id,
                order: gameInfo.order
              }),
              setCachedGameMode({ id: gameInfo.id, mode: gameInfo.mode })
            )
          );

          return null;
        }

        return {
          id: gameInfo.id,
          order: gameInfo.order,
          mode: gameInfo.mode
        };
      });

      const filteredGames = notCachedGameIds.filter(Boolean) as {
        id: string;
        order: ECasinoGameViewOrder;
        mode: ECasinoGameMode;
      }[];

      // preserving the game ids in the store
      filteredGames.forEach(({ id, mode }) => {
        dispatch(addCachedGameId(id));
        dispatch(setCachedGameActive(id));
        dispatch(setCachedGameMode({ id, mode }));
      });
      const filteredGameIds = filteredGames.map(game => game.id);
      fetchGamesByIds(
        filteredGameIds,
        true,
        true,
        !!SpringConfigs.DECENTRALIZED_CASINO_URL
      ).then(games => {
        games.forEach(game => {
          dispatch(addCachedGame(gameMapper(game)));
          const filteredGame = filteredGames.find(
            filteredGame => filteredGame.id === game.extearnal_game_id
          );

          if (filteredGame) {
            dispatch(
              setCachedGameOrder({
                id: game.extearnal_game_id,
                order: filteredGame.order
              })
            );
          }
        });
      });
    };

    const checkAuthorizedToOpen = () => {
      const isRealModePresentInUrl = gamesInfo.some(
        ({ mode }) => mode === ECasinoGameMode.REAL
      );

      const isUserLoggedIn = UtilsAccount.checkIsUserLoggedIn();

      if (isRealModePresentInUrl && !isUserLoggedIn) {
        UtilsCasino.reconstructAndRedirectToGameView();

        return true;
      }

      return false;
    };

    const shouldAuthorize = checkAuthorizedToOpen();

    if (!shouldAuthorize) {
      checkAndAddGamesInCache();
    }
  }, [JSON.stringify(searchParams)]);

  useLayoutEffect(() => {
    if (
      searchParams.mode === ECasinoGameMode.REAL &&
      !isLoggedIn &&
      !searchParams.login &&
      !user.pending
    ) {
      UtilsCasino.reconstructAndRedirectToGameView();
    }
  }, [isLoggedIn, user.pending]);

  useEffect(() => {
    if (searchParams.multiview !== CASINO_MULTIVIEW_ENABLED_QUERY_VALUE) {
      setActiveView(ECasinoGameView.SINGLE);

      return;
    }

    setActiveView(ECasinoGameView.MULTI);
  }, [searchParams.multiview]);

  if (!isThereAnyActiveGame) {
    return null;
  }

  return (
    <Portal>
      <>
        <MultiviewGamesSameGameNotice
          open={sameGameNoticeOpen}
          setOpen={setSameGameNoticeOpen}
        />
        <CasinoLimitModal />
        <CasinoGameViewContainerProvider
          value={{
            view: activeView,
            onViewChange: setActiveView,
            externalCategory,
            mainCategory,
            sameGameNoticeOpen,
            setSameGameNoticeOpen
          }}
        >
          <div
            style={{ '--my-var': `${headerHeight}px` } as CSSProperties}
            id={CasinoDom.MODAL_CONTAINER}
          >
            <Modal
              zIndex={!isMobile() ? ZINDEX_HIGH_3 : ZINDEX_HIGH_5}
              className={cc([
                'x-casinoGameViewContainerModal',
                {
                  'x-casinoGameViewContainerModal__header--visible':
                    isHeaderVisible
                }
              ])}
              style={heightStyle}
              width="100%"
              maxWidth="100%"
              hideBorder
              closable={false}
              maskClosable={false}
              wrapClassName={
                isHeaderVisible ? 'modal__with__fixed__header' : ''
              }
              getContainer={`${CasinoDom.MODAL_CONTAINER}`}
              bodyStyle={heightStyle}
              maskStyle={maskStyle}
              visible
            >
              <div
                className={cc([
                  'x-casinoGameViewContainerModal__container',
                  {
                    'x-casinoGameViewContainerModal__container--IOS': IS_IOS
                  }
                ])}
              >
                <CasinoOpenGameConfigsProvider
                  value={{ onOpenGame: handleOpenGameMain }}
                >
                  <div
                    className={cc([
                      'x-casinoGameViewContainerModal__container__item',
                      {
                        'x-casinoGameViewContainerModal__container__item--main':
                          activeView === ECasinoGameView.SINGLE
                      }
                    ])}
                  >
                    {debouncedSortedActiveGames[0]?.data && (
                      <GameView
                        game={debouncedSortedActiveGames[0].data}
                        mode={
                          debouncedSortedActiveGames[0].mode ||
                          searchParamsModeForMobile ||
                          undefined
                        }
                        key={debouncedSortedActiveGames[0].data.id}
                        onModeChange={mode =>
                          handleModeChangeMain(
                            debouncedSortedActiveGames[0].data,
                            mode
                          )
                        }
                      />
                    )}
                  </div>
                </CasinoOpenGameConfigsProvider>
                {activeView === ECasinoGameView.MULTI && (
                  <CasinoOpenGameConfigsProvider
                    value={{ onOpenGame: handleOpenGameSecondary }}
                  >
                    <div className="x-casinoGameViewContainerModal__container__item">
                      {debouncedSortedActiveGames[1]?.data && (
                        <GameView
                          game={debouncedSortedActiveGames[1].data}
                          mode={debouncedSortedActiveGames[1].mode}
                          key={debouncedSortedActiveGames[1].data.id}
                          onModeChange={mode =>
                            handleModeChangeSecondary(
                              debouncedSortedActiveGames[1].data,
                              mode
                            )
                          }
                        />
                      )}
                    </div>
                  </CasinoOpenGameConfigsProvider>
                )}
              </div>
            </Modal>
            <SingleviewManager />
            <MultiviewManager />
          </div>
          <MultiviewSwitcher />
        </CasinoGameViewContainerProvider>
      </>
    </Portal>
  );
};
